import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Container, Form, Row, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import {
  deletePromoCode,
  listPromoCodesAdmin,
} from "../../actions/promoCodeActions";
import { PROMO_CODE_DELETE_RESET } from "../../constants/promoCodeConstants";

function formatMoney(value = 0, currency = "GBP") {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency,
    }).format(Number(value || 0));
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
}

function formatDiscount(promo = {}) {
  if (promo.discountType === "fixed") {
    return formatMoney(promo.discountValue, promo.currency || "GBP");
  }

  return `${Number(promo.discountValue || 0)}%`;
}

function getPromoStatus(promo = {}) {
  if (promo.isDeleted) return { label: "Deleted", bg: "secondary" };
  if (!promo.isActive) return { label: "Inactive", bg: "warning", text: "dark" };

  const now = Date.now();
  const startsAt = promo.startsAt ? new Date(promo.startsAt).getTime() : 0;
  const endsAt = promo.endsAt ? new Date(promo.endsAt).getTime() : 0;

  if (startsAt && startsAt > now) {
    return { label: "Scheduled", bg: "info" };
  }

  if (endsAt && endsAt < now) {
    return { label: "Expired", bg: "secondary" };
  }

  return { label: "Active", bg: "success" };
}

export default function AdminPromoCodesScreen() {
  const dispatch = useDispatch();
  const [status, setStatus] = useState("active");

  const listState = useSelector((state) => state.promoCodeAdminList || {});
  const deleteState = useSelector((state) => state.promoCodeDelete || {});

  const { loading, error, promoCodes = [], total = 0 } = listState;
  const { success: successDelete, error: deleteError } = deleteState;

  const params = useMemo(() => ({ status, limit: 100 }), [status]);

  useEffect(() => {
    dispatch(listPromoCodesAdmin(params));
  }, [dispatch, params, successDelete]);

  useEffect(() => {
    if (successDelete) dispatch({ type: PROMO_CODE_DELETE_RESET });
  }, [dispatch, successDelete]);

  const deleteHandler = (id) => {
    if (window.confirm("Delete this promo code?")) {
      dispatch(deletePromoCode(id));
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>Admin • Promo Codes</h1>
          <div className="text-muted">Manage checkout promo-code discounts</div>
        </Col>

        <Col md="auto">
          <Form.Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Filter promo codes"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="deleted">Deleted</option>
            <option value="all">All</option>
          </Form.Select>
        </Col>

        <Col md="auto">
          <Link to="/admin/promo-codes/new" className="btn btn-dark">
            Create promo code
          </Link>
        </Col>
      </Row>

      {deleteError ? <Message variant="danger">{deleteError}</Message> : null}
      {error ? <Message variant="danger">{error}</Message> : null}
      {loading ? <Loader /> : null}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Code</th>
            <th>Discount</th>
            <th>Minimum</th>
            <th>Target</th>
            <th>Usage</th>
            <th>Status</th>
            <th>Edit</th>
            <th>Delete</th>
          </tr>
        </thead>
        <tbody>
          {promoCodes.map((promo) => {
            const statusInfo = getPromoStatus(promo);

            return (
              <tr
                key={promo.id || promo._id}
                style={promo.isDeleted ? { opacity: 0.6 } : undefined}
              >
                <td>
                  <strong>{promo.code}</strong>
                  {promo.name ? <div className="text-muted">{promo.name}</div> : null}
                </td>
                <td>{formatDiscount(promo)}</td>
                <td>
                  {promo.minimumSubtotal
                    ? formatMoney(promo.minimumSubtotal, promo.currency)
                    : "None"}
                </td>
                <td>
                  {Array.isArray(promo.productIds) && promo.productIds.length
                    ? `${promo.productIds.length} products`
                    : "All products"}
                </td>
                <td>
                  {Number(promo.usedCount || 0)}
                  {promo.usageLimit ? ` / ${promo.usageLimit}` : ""}
                </td>
                <td>
                  <Badge bg={statusInfo.bg} text={statusInfo.text}>
                    {statusInfo.label}
                  </Badge>
                </td>
                <td>
                  <Link to={`/admin/promo-codes/${promo.id || promo._id}`}>
                    Edit
                  </Link>
                </td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={promo.isDeleted}
                    onClick={() => deleteHandler(promo.id || promo._id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            );
          })}

          {!promoCodes.length ? (
            <tr>
              <td colSpan={8} className="text-center text-muted">
                No promo codes found
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>

      <div className="text-muted small">{total} promo codes</div>
    </Container>
  );
}
